import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { AnalyticsPageName } from 'src/analytics/analytics.models'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import * as style from 'src/app/CardFeedCreate/CardFeedCreate.style'
import StepContactBank from 'src/app/CardFeedCreate/StepContactBank'
import StepEnterFeedDetails from 'src/app/CardFeedCreate/StepEnterFeedDetails'
import Stepper from 'src/app/CardFeedCreate/Stepper'
import StepReviewTerms from 'src/app/CardFeedCreate/StepReviewTerms'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import LayoutPage from 'src/component-layout/LayoutPage/LayoutPage'
import Box from 'src/components/Box/Box'
import { useTranslate } from 'src/localization/Trns'
import { UserFeature } from 'src/models/user.models'
import { cardFeedDetailsUrl, homeUrl } from 'src/navigation/urls'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import { CreateCardFeedRequest } from 'src/queries/cardFeeds/cardFeeds.models'
import { useCreateCardFeed } from 'src/queries/cardFeeds/domain/cardFeeds.queries'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { selectUserExplicitOrganizationListModelFromState } from 'src/redux/selectors/data/organizations.selectors'

export type WizardStep = 1 | 2 | 3
export const NETWORK_VALUES = ['mastercard', 'visa'] as const
export type CardNetwork = (typeof NETWORK_VALUES)[number]

export type FieldKey =
  | 'deliveryId'
  | 'regionId'
  | 'processorId'
  | 'issuerId'
  | 'companyId'

export type FieldDef = {
  key: FieldKey
  labelKey: string
  placeholderKey: string
  testId: string
  tooltipKey?: string
}

export const NETWORK_FIELDS: Record<CardNetwork, readonly FieldDef[]> = {
  mastercard: [
    {
      key: 'deliveryId',
      labelKey: 'Delivery ID',
      placeholderKey: 'Enter Delivery ID e.g. G1234567',
      testId: 'card-feed-create-delivery-id',
      tooltipKey:
        'The Delivery ID is a unique identifier your bank provides to route transactions to Extend.',
    },
  ],
  visa: [
    {
      key: 'regionId',
      labelKey: 'Region ID',
      placeholderKey: 'Enter Region ID',
      testId: 'card-feed-create-region-id',
    },
    {
      key: 'processorId',
      labelKey: 'Processor ID',
      placeholderKey: 'Enter Processor ID',
      testId: 'card-feed-create-processor-id',
    },
    {
      key: 'issuerId',
      labelKey: 'Issuer ID',
      placeholderKey: 'Enter Issuer ID',
      testId: 'card-feed-create-issuer-id',
    },
    {
      key: 'companyId',
      labelKey: 'Company ID',
      placeholderKey: 'Enter Company ID',
      testId: 'card-feed-create-company-id',
    },
  ],
}

export type FeedFields = Partial<Record<FieldKey, string>>

export const isFeedDetailsValid = (
  network: CardNetwork | undefined,
  fields: FeedFields,
): boolean => {
  if (network === undefined) return false
  return NETWORK_FIELDS[network].every(
    (def) => (fields[def.key] ?? '').trim().length > 0,
  )
}

export const buildCreateCardFeedRequest = (
  network: CardNetwork,
  fields: FeedFields,
): CreateCardFeedRequest => {
  if (network === 'mastercard') {
    return {
      cardNetwork: 'MASTERCARD',
      deliveryId: fields.deliveryId ?? '',
    }
  }
  return {
    cardNetwork: 'VISA',
    regionId: fields.regionId ?? '',
    processorId: fields.processorId ?? '',
    issuerId: fields.issuerId ?? '',
    companyId: fields.companyId ?? '',
  }
}

const CardFeedCreate: React.FC = () => {
  const translate = useTranslate()
  const { analytics } = useAnalyticsContext()
  const history = useHistory()
  const dispatch = useDispatch()

  const { data: featuresData, isLoading: isFeaturesLoading } =
    useQueryUserFeatures([UserFeature.BULK_CREDIT_CARD_ENROLLMENT])
  const bulkCreditCardEnrollmentEnabled =
    featuresData?.BULK_CREDIT_CARD_ENROLLMENT ?? false
  const { enable_card_feeds: cardFeedsEnabled } =
    useSelector(selectFeatureFlaggerFromState) ?? {}
  const currentOrganization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )

  const isAllowed = bulkCreditCardEnrollmentEnabled && !!cardFeedsEnabled

  const [step, setStep] = useState<WizardStep>(1)
  const [network, setNetwork] = useState<CardNetwork | undefined>()
  const [fields, setFields] = useState<FeedFields>({})
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [submitError, setSubmitError] = useState<string | undefined>()

  const { mutate: createCardFeed, isPending: isSubmitting } = useCreateCardFeed(
    {
      onSuccess: (data) => {
        analytics.track('card feeds create', {
          action: 'submit success',
          network: data.cardNetwork,
          id: data.id,
        })
        dispatch(
          createSetIsToastOpen(true, {
            toastMessage: (
              <p>
                {translate(
                  'Card feed created — your bank will activate it shortly.',
                )}
              </p>
            ),
          }),
        )
        history.push(cardFeedDetailsUrl(data.id))
      },
      onError: (error) => {
        const message =
          error.response?.data?.error ??
          translate('Something went wrong. Please try again.')
        setSubmitError(message)
        analytics.track('card feeds create', {
          action: 'submit failed',
          network,
        })
      },
    },
  )

  // Same defensive gate as CardFeeds list page: a direct URL must redirect
  // home when either flag is off, but wait for the feature query to settle.
  useEffect(() => {
    if (isFeaturesLoading) return
    if (!isAllowed) {
      history.replace(homeUrl())
    }
  }, [isAllowed, isFeaturesLoading, history])

  useEffect(() => {
    if (!isAllowed) return
    analytics.page(AnalyticsPageName.OrgSettingsCardFeedsCreate)
    analytics.track('card feeds create', { action: 'viewed' })
  }, [analytics, isAllowed])

  useEffect(() => {
    if (!isAllowed) return
    analytics.track('card feeds create', { action: 'step viewed', step })
  }, [analytics, isAllowed, step])

  if (!isAllowed) {
    return null
  }

  const handleNetworkChange = (next: CardNetwork | undefined): void => {
    if (next !== network) {
      setTermsAccepted(false)
      setSubmitError(undefined)
      setFields({})
    }
    setNetwork(next)
  }

  const handleConfirm = (): void => {
    if (network === undefined || !currentOrganization?.id) return
    setSubmitError(undefined)
    createCardFeed({
      organizationId: currentOrganization.id,
      body: buildCreateCardFeedRequest(network, fields),
    })
  }

  return (
    <>
      <Helmet>
        <title>{translate('Card Feeds')}</title>
      </Helmet>
      <LayoutPage
        title={<span>{translate('Card Feeds')}</span>}
        subtitle={
          <span css={style.subtitle}>
            {translate(
              'Connect your bank to unlock real-time transaction data and seamlessly manage employee card spend.',
            )}
          </span>
        }
      >
        <div css={style.wizardLayout}>
          <Box noPadding outlineBorder css={style.stepperCard}>
            <Stepper currentStep={step} />
          </Box>
          <Box noPadding outlineBorder css={style.contentCard}>
            {step === 1 && <StepContactBank onNext={(): void => setStep(2)} />}
            {step === 2 && (
              <StepEnterFeedDetails
                network={network}
                fields={fields}
                onNetworkChange={handleNetworkChange}
                onFieldsChange={setFields}
                onBack={(): void => setStep(1)}
                onNext={(): void => setStep(3)}
              />
            )}
            {step === 3 && (
              <StepReviewTerms
                network={network}
                termsAccepted={termsAccepted}
                onTermsAcceptedChange={setTermsAccepted}
                onBack={(): void => {
                  setSubmitError(undefined)
                  setStep(2)
                }}
                onConfirm={handleConfirm}
                isSubmitting={isSubmitting}
                submitError={submitError}
              />
            )}
          </Box>
        </div>
      </LayoutPage>
    </>
  )
}

export default CardFeedCreate
